import { type useAdProvidersSettings } from '@/composables/ad-providers/use-ad-settings'
import { type Config } from '@/composables/use-clid-helpers'
import { NATIVE_APP_CLIDS } from '@/constants/general'
import { ProviderKey } from '@/modules/adv/ad-service'
import { type AdProvidersSettings, AdProviderTypes } from '@/types'

export function getCurrentProvidersSettings({
    clid,
    providersSettings,
    clidConfig,
    isGameDistribution,
    isOppoWL,
    isExportOnWidget,
    isPartnerEmbed,
    isNativeAppEnv,
    isGoogleProvidersEnabled,
}: {
    clid: string
    providersSettings: ReturnType<typeof useAdProvidersSettings>
    clidConfig: Config
    isGameDistribution: boolean
    isOppoWL: boolean
    isExportOnWidget: boolean
    isPartnerEmbed: boolean
    isNativeAppEnv: boolean
    isGoogleProvidersEnabled: boolean
}): Partial<Record<ProviderKey, AdProvidersSettings[]>> {
    const NATIVE_APP_PROVIDERS = {
        [NATIVE_APP_CLIDS.directAndroidTest]: providersSettings.APP_NATIVE_ANDROID_TEST_PROVIDERS_SETTINGS,
        [NATIVE_APP_CLIDS.directAndroid]: providersSettings.APP_NATIVE_ANDROID_PROVIDERS_SETTINGS,
        [NATIVE_APP_CLIDS.directIosTest]: providersSettings.APP_NATIVE_IOS_TEST_PROVIDERS_SETTINGS,
        [NATIVE_APP_CLIDS.directIos]: providersSettings.APP_NATIVE_IOS_PROVIDERS_SETTINGS,
    }

    const GOOGLE_PROVIDER_TYPES = new Set([
        AdProviderTypes.GOOGLE_AD,
        AdProviderTypes.GOOGLE_BANNER,
        AdProviderTypes.GOOGLE_INSTREAM,
    ])

    function filterGoogleProviders(providers: AdProvidersSettings[]): AdProvidersSettings[] {
        return isGoogleProvidersEnabled
            ? providers
            : providers.filter((p) => !GOOGLE_PROVIDER_TYPES.has(p.type as AdProviderTypes))
    }

    if (isOppoWL) {
        return {
            default: [providersSettings.GOOGLE_AD_SENSE_SETTINGS],
        }
    }

    if (isGameDistribution) {
        return {
            default: [providersSettings.GAME_DISTRIBUTION_PROVIDER_SETTINGS],
        }
    }

    let providers: AdProvidersSettings[] = []

    // custom
    if (clidConfig.inGameProviderSettings && isPartnerEmbed) {
        providers = clidConfig.inGameProviderSettings
    } else {
        // for apps
        if (isNativeAppEnv && NATIVE_APP_PROVIDERS[clid]) {
            providers = NATIVE_APP_PROVIDERS[clid] as AdProvidersSettings[]
        }
        // for isExportOnWidget / partners without inGameProviderSettings
        else if (isExportOnWidget) {
            providers = providersSettings.AD_PROVIDER_SETTINGS_FOR_EXPORT_GAME_PAGE_ON_WIDGET
        } else {
            providers = providersSettings.AD_PROVIDER_SETTINGS_FOR_EXPORT_GAME_PAGE
        }
    }

    providers = filterGoogleProviders(providers)

    let fallbackProviders: AdProvidersSettings[] = []

    // custom fallback for partners
    if (isPartnerEmbed) {
        fallbackProviders = clidConfig.fallBackProviders ?? providersSettings.FALLBACK_AD_PROVIDER_SETTINGS_PARTNER
    }
    // for isExportOnWidget; not using fallback for apps
    else if (!isNativeAppEnv) {
        fallbackProviders = providersSettings.FALLBACK_AD_PROVIDER_SETTINGS_ON_PORTAL
    }

    return {
        default: filterGoogleProviders(providersSettings.AD_PROVIDER_SETTINGS_ON_PORTAL),
        interstitial: [...providers, ...fallbackProviders],
        interstitial_midroll: [...providers, ...fallbackProviders],
        interstitial_preroll: [...providers, ...fallbackProviders],
        rewarded: [...providers, ...fallbackProviders],
    }
}
